import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { mountInquiryForm, prefillEmpty } from './formEngine';
import logo from './assets/acuity-logo.png';

const inquiriesAccessCache = new Map();

function inquiriesAccessKey(userId, currentRole) {
  return [
    userId,
    currentRole?.groups_periods_id || '',
    currentRole?.group_name || '',
    currentRole?.relationship_type || '',
    currentRole?.parent_group_name || '',
  ].join('|');
}

async function fetchInquiriesAccess(user, token, currentRole) {
  const params = new URLSearchParams({ userId: user.id });
  if (currentRole?.groups_periods_id) params.append('groups_periods_id', currentRole.groups_periods_id);
  if (currentRole?.group_name) params.append('group_name', currentRole.group_name);
  if (currentRole?.relationship_type) params.append('relationship_type', currentRole.relationship_type);
  if (currentRole?.parent_group_name) params.append('parent_group_name', currentRole.parent_group_name);

  const res = await fetch(`/api/page-configurations/page/inquiries?${params}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const data = await res.json();
  return data.success === true && data.data?.hasAccess === true;
}

function useCanViewInquiries() {
  const { isAuthenticated, user, token, currentRole, loadingRole } = useAuth();
  const [canView, setCanView] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !token || loadingRole) {
      setCanView(false);
      return undefined;
    }

    const key = inquiriesAccessKey(user.id, currentRole);
    let cancelled = false;

    if (!inquiriesAccessCache.has(key)) {
      inquiriesAccessCache.set(
        key,
        fetchInquiriesAccess(user, token, currentRole).catch((err) => {
          inquiriesAccessCache.delete(key);
          throw err;
        })
      );
    }

    inquiriesAccessCache
      .get(key)
      .then((allowed) => {
        if (!cancelled) setCanView(allowed === true);
      })
      .catch(() => {
        if (!cancelled) setCanView(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    user,
    token,
    loadingRole,
    currentRole?.groups_periods_id,
    currentRole?.group_name,
    currentRole?.relationship_type,
    currentRole?.parent_group_name,
  ]);

  return canView;
}

export function LandingAuthButton() {
  const { isAuthenticated } = useAuth();
  const canViewInquiries = useCanViewInquiries();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Link
        className="btn btn-ghost"
        to="/auth"
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      >
        Sign in
      </Link>
    );
  }

  if (canViewInquiries) {
    return (
      <Link className="btn btn-ghost btn-inquiries" to="/inquiries">
        Return to inquiries
      </Link>
    );
  }

  return (
    <Link className="btn btn-ghost" to="/dashboard">
      Dashboard
    </Link>
  );
}

export function SignedInNotice() {
  const { isAuthenticated } = useAuth();
  const canViewInquiries = useCanViewInquiries();
  if (!isAuthenticated) return null;
  return (
    <p className="signed-in-banner">
      You&apos;re already signed in. You can still submit this request, or{' '}
      {canViewInquiries ? (
        <Link to="/inquiries">return to inquiries</Link>
      ) : (
        <>
          go to your <Link to="/dashboard">dashboard</Link>
        </>
      )}
      .
    </p>
  );
}

export function LandingHeader({ logoTo = '/onboarding' }) {
  const { isAuthenticated } = useAuth();

  return (
    <header className="site-header">
      <div className="wrap">
        <Link to={logoTo} aria-label="Acuity Learn home">
          <img src={logo} alt="Acuity Learn" />
        </Link>
        <div className="header-actions">
          {isAuthenticated ? (
            <span className="signed-in-note">Signed in</span>
          ) : null}
          <LandingAuthButton />
        </div>
      </div>
    </header>
  );
}

export function LandingFooter({ note }) {
  return (
    <footer className="site-footer">
      <div className="wrap">
        <img src={logo} alt="Acuity Learn" />
        <p>{note}</p>
      </div>
    </footer>
  );
}

function displayName(user) {
  if (!user) return '';
  return [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
}

export function useInquiryForm(schema, { formId, submitId }) {
  const { user, isAuthenticated } = useAuth();
  const formRef = useRef(null);
  const submitRef = useRef(null);
  const confirmRef = useRef(null);
  const confirmNameRef = useRef(null);
  const confirmRefEl = useRef(null);
  const errorRef = useRef(null);
  const formHeadRef = useRef(null);
  const formFootRef = useRef(null);

  useEffect(() => {
    const form = formRef.current;
    const submitBtn = submitRef.current;
    if (!form || !submitBtn) return undefined;

    const unmount = mountInquiryForm({
      schema,
      form,
      submitBtn,
      confirmEl: confirmRef.current,
      confirmNameEl: confirmNameRef.current,
      confirmRefEl: confirmRefEl.current,
      errorEl: errorRef.current,
      formHeadEl: formHeadRef.current,
      formFootEl: formFootRef.current,
    });

    return unmount;
  }, [schema, formId, submitId]);

  useEffect(() => {
    const form = formRef.current;
    if (!form || !isAuthenticated || !user) return;
    prefillEmpty(form, {
      [schema.map.contactName]: displayName(user),
      email: user.email || '',
    });
  }, [isAuthenticated, user, schema]);

  return {
    formRef,
    submitRef,
    confirmRef,
    confirmNameRef,
    confirmRefEl,
    errorRef,
    formHeadRef,
    formFootRef,
    formId,
    submitId,
  };
}
